import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminTopline, Panel } from "../../components/console";
import { Lock } from "../../components/icons";
import { api, describeError } from "../../lib/api";
import type { DocumentType } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { canonicalRole, ROLE_LABEL_LO } from "../../lib/roles";
import { num } from "../../lib/format";
import {
  fetchTemplates,
  DOC_TYPE_MODE_LABEL,
  docTypeMode,
  isSignThrough,
  setModeInput,
  useDeleteDocumentType,
  useSaveDocumentType,
} from "../../lib/documentTypes";
import { DocumentLayoutModal } from "./DocumentLayoutModal";
import { FormSchemaEditor } from "./FormSchemaEditor";
import { HOME_GROUPS } from "../../lib/documentTypes";
import {
  CreateDocumentTypeModal,
  EditDocumentTypeModal,
} from "./DocumentTypeEditor";

const CATEGORY_LABEL_LO: Record<string, string> = {
  A: "ກ. ຄ່າບໍລິການ",
  B: "ຂ. ຄ່າທຳນຽມຊັບສິນ",
  C: "ຄ. ອາກອນ/ສະແຕັມ",
  D: "ງ. ລາຍຮັບອື່ນໆ",
};
const CATEGORIES = ["A", "B", "C", "D"] as const;

const KNOWN_GROUPS = new Set(HOME_GROUPS.map((g) => g.key));
/** ค่าที่ไม่รู้จักตกไป "ອື່ນໆ" เหมือนแอป (HomeGroup.fromWire) — ห้ามหายจากตาราง */
function groupOf(d: DocumentType): string {
  return KNOWN_GROUPS.has(d.home_group) ? d.home_group : "other";
}

/** A2 ก่อน A10 — เรียงรหัสแบบตัวเลขในระเบียบ ไม่ใช่ตามตัวอักษร */
function byCode(a: DocumentType, b: DocumentType): number {
  return a.code.localeCompare(b.code, undefined, { numeric: true });
}

async function fetchDocumentTypes(): Promise<DocumentType[]> {
  const { data, error, response } = await api.GET("/v1/document-types", {
    params: { query: { limit: 200 } },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items.filter((d) => d.deleted_at === null);
}

/** จำนวนช่องในฟอร์มกรอก (ไม่นับช่องระบบของ wizard) */
function formFieldCount(d: DocumentType): number {
  const props = (
    d.form_schema as
      { properties?: Record<string, { "x-system"?: unknown }> } | undefined
  )?.properties;
  return props
    ? Object.values(props).filter((p) => p["x-system"] !== true).length
    : 0;
}

/** อัตราตามระเบียบ: null = ไม่ได้กำหนด, is_exempt = ยกเว้น */
function feeText(d: DocumentType): string {
  if (d.is_exempt) return "ຍົກເວັ້ນ";
  if (d.fee_lak == null) return "—";
  return num(d.fee_lak);
}

/**
 * แคตตาล็อกเอกสาร + อัตรา — หน้าเดียวทุกระดับ แต่สิทธิ์ต่างกันตาม role
 *
 * - `super_admin` แก้ตรง/เพิ่ม/เปิด-ปิดได้ (`/v1/admin/document-types`)
 * - ที่เหลืออ่านอย่างเดียว — จอ "เมืองเสนอ → แขวงอนุมัติ" ถูกตัดออกจาก web-admin แล้ว
 *   (API `/v1/admin/fee-proposals` ยังอยู่ แต่ไม่มีจอเรียกใช้)
 */
export function DistrictCatalogPage() {
  const role = getCurrentRole();
  const canonical = role ? canonicalRole(role) : null;
  const isAdmin = canonical === "super_admin";

  const types = useQuery({
    queryKey: ["document-types"],
    queryFn: fetchDocumentTypes,
  });
  const templates = useQuery({
    queryKey: ["document-templates"],
    queryFn: fetchTemplates,
  });

  const [search, setSearch] = useState("");
  // null = ทุกกลุ่ม (ตารางแบ่งหัวข้อตามกลุ่ม) · กดชิปเดิมซ้ำ = กลับไปทุกกลุ่ม
  const [group, setGroup] = useState<string | null>(null);
  // ตัวกรองเสริม: เฉพาะชนิดที่ตั้ง ເຊັນຜ່ານ (ใช้ร่วมกับกลุ่ม/ค้นหาได้)
  const [signOnly, setSignOnly] = useState(false);
  const [editing, setEditing] = useState<DocumentType | null>(null);
  const [creating, setCreating] = useState(false);
  const [layoutFor, setLayoutFor] = useState<DocumentType | null>(null);
  const [schemaFor, setSchemaFor] = useState<DocumentType | null>(null);
  const save = useSaveDocumentType();
  const remove = useDeleteDocumentType();

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (types.data ?? [])
      .filter((d) => group === null || groupOf(d) === group)
      .filter((d) => !signOnly || isSignThrough(d))
      .filter(
        (d) =>
          q === "" ||
          d.name_lo.toLowerCase().includes(q) ||
          d.code.toLowerCase().includes(q),
      )
      .sort(byCode);
  }, [types.data, search, group, signOnly]);

  // แบ่งหัวข้อตามกลุ่มของหน้า Home ตามลำดับเดียวกับแอป — กลุ่มว่างไม่แสดง
  const groups = useMemo(
    () =>
      HOME_GROUPS.map((g) => ({
        ...g,
        items: rows.filter((d) => groupOf(d) === g.key),
      })).filter((g) => g.items.length > 0),
    [rows],
  );

  const countByGroup = useMemo(() => {
    const out = new Map<string, number>();
    for (const d of types.data ?? [])
      out.set(groupOf(d), (out.get(groupOf(d)) ?? 0) + 1);
    return out;
  }, [types.data]);

  const signThroughCount = useMemo(
    () => (types.data ?? []).filter(isSignThrough).length,
    [types.data],
  );

  const countByCategory = useMemo(() => {
    const out = new Map<string, { total: number; active: number }>();
    for (const d of types.data ?? []) {
      const c = out.get(d.category_code) ?? { total: 0, active: 0 };
      c.total += 1;
      if (d.is_active) c.active += 1;
      out.set(d.category_code, c);
    }
    return out;
  }, [types.data]);

  const anyError = types.error ?? templates.error ?? save.error;

  return (
    <div>
      <AdminTopline
        title="ລາຍການເອກະສານ ແລະ ອັດຕາຄ່າທຳນຽມ"
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
        actions={
          isAdmin ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setCreating(true);
              }}
            >
              ເພີ່ມເອກະສານ
            </button>
          ) : (
            <span className="lock-note">
              <Lock size={13} />
              ແກ້ໄຂອັດຕາໄດ້ສະເພາະຜູ້ບໍລິຫານລະບົບ
            </span>
          )
        }
      />

      {remove.error ? (
        <div role="alert" className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          {remove.error.message}
        </div>
      ) : null}

      {anyError ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {anyError.message}
        </div>
      ) : null}

      {/* สรุปตามหมวดค่าธรรมเนียมในระเบียบ (ກ–ງ) — ตัวกรองอยู่ในตารางด้านล่าง (กลุ่มแบบหน้า Home) */}
      <div className="kpi-grid">
        {CATEGORIES.map((code) => {
          const c = countByCategory.get(code) ?? { total: 0, active: 0 };
          return (
            <article key={code} className="kpi">
              <div className="lbl">{CATEGORY_LABEL_LO[code]}</div>
              <div className="val num">{num(c.total)}</div>
              <div className="delta" style={{ color: "var(--ink-400)" }}>
                ເປີດໃຊ້ <span className="num">{num(c.active)}</span>
              </div>
            </article>
          );
        })}
      </div>

      <Panel title="ລາຍການເອກະສານ">
        <input
          className="filter-search"
          style={{ maxWidth: "20rem", marginBottom: "var(--space-3)" }}
          placeholder="ຄົ້ນຫາເອກະສານ..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        {/* ชิปกลุ่มแบบหน้า Home ของแอป — กดเพื่อดูเฉพาะกลุ่ม กดซ้ำ/ກດ "ທັງໝົດ" เพื่อกลับ */}
        <div className="group-chips" role="group" aria-label="ກັ່ນຕອງຕາມປະເພດ">
          <button
            type="button"
            className={group === null ? "group-chip is-active" : "group-chip"}
            aria-pressed={group === null}
            onClick={() => {
              setGroup(null);
            }}
          >
            ທັງໝົດ <span className="num">{num(types.data?.length ?? 0)}</span>
          </button>
          {HOME_GROUPS.filter((g) => (countByGroup.get(g.key) ?? 0) > 0).map(
            (g) => {
              const selected = group === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  className={selected ? "group-chip is-active" : "group-chip"}
                  aria-pressed={selected}
                  onClick={() => {
                    setGroup(selected ? null : g.key);
                  }}
                >
                  {g.labelLo}{" "}
                  <span className="num">
                    {num(countByGroup.get(g.key) ?? 0)}
                  </span>
                </button>
              );
            },
          )}
          <button
            type="button"
            className={signOnly ? "group-chip is-active" : "group-chip"}
            aria-pressed={signOnly}
            onClick={() => {
              setSignOnly(!signOnly);
            }}
          >
            ເຊັນຜ່ານ <span className="num">{num(signThroughCount)}</span>
          </button>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ລະຫັດ</th>
                <th>ຊື່ເອກະສານ</th>
                <th>ໝວດ</th>
                <th className="is-right">ຄ່າທຳນຽມ (ກີບ)</th>
                <th className="is-right">ສະແຕັມ / ສະບັບ</th>
                <th>ສະຖານະ</th>
                <th>ຟອມກອກ</th>
                <th>ເຊັນຜ່ານ</th>
                <th>ແມ່ແບບ (AI)</th>
                <th className="is-right">
                  {isAdmin ? "ຈັດການ" : "ການແກ້ອັດຕາ"}
                </th>
              </tr>
            </thead>
            <tbody>
              {types.isPending ? (
                <tr>
                  <td colSpan={10} className="data-table__empty">
                    ກຳລັງໂຫຼດ…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="data-table__empty">
                    ບໍ່ພົບເອກະສານທີ່ຊອກຫາ
                  </td>
                </tr>
              ) : (
                groups.map((g) => [
                  <tr key={`group-${g.key}`} className="data-table__group">
                    <td colSpan={10}>
                      <span className="data-table__group-title">
                        {g.labelLo}
                      </span>
                      <span className="caption">
                        {" "}
                        · <span className="num">
                          {num(g.items.length)}
                        </span>{" "}
                        ລາຍການ
                      </span>
                    </td>
                  </tr>,
                  ...g.items.map((d) => {
                    return (
                      <tr key={d.id}>
                        <td className="num">{d.code}</td>
                        <td>{d.name_lo}</td>
                        <td>
                          <span className="status-badge status-badge--info">
                            {d.category_code || "—"}
                          </span>
                        </td>
                        <td className="is-right num">
                          {feeText(d)}
                          {!d.is_exempt && d.fee_lak != null && d.unit_lo ? (
                            <span className="caption"> / {d.unit_lo}</span>
                          ) : null}
                        </td>
                        <td className="is-right num">
                          {d.stamp_lak > 0 ? num(d.stamp_lak) : "—"}
                        </td>
                        <td>
                          <span
                            className={
                              docTypeMode(d) === "on"
                                ? "status-badge status-badge--success"
                                : docTypeMode(d) === "development"
                                  ? "status-badge status-badge--pending"
                                  : "status-badge"
                            }
                          >
                            {DOC_TYPE_MODE_LABEL[docTypeMode(d)]}
                          </span>
                        </td>
                        <td>
                          {isSignThrough(d) ? (
                            <span style={{ color: "var(--ink-400)" }}>—</span>
                          ) : (
                            <button
                              type="button"
                              className="link-action"
                              onClick={() => {
                                setSchemaFor(d);
                              }}
                            >
                              <span className="num">
                                {num(formFieldCount(d))}
                              </span>{" "}
                              ຊ່ອງ
                            </button>
                          )}
                        </td>
                        <td>
                          {isSignThrough(d) ? (
                            <span className="status-badge status-badge--info">ເຊັນຜ່ານ</span>
                          ) : (
                            <span style={{ color: "var(--ink-400)" }}>ບໍ່</span>
                          )}
                        </td>
                        <td>
                          {/* แม่แบบที่ AI สร้างจากไฟล์ตัวอย่าง (ADR-0028) — สถานะโหลดในจอเอง */}
                          {isSignThrough(d) ? (
                            <span style={{ color: "var(--ink-400)" }}>—</span>
                          ) : (
                            <button
                              type="button"
                              className="link-action"
                              onClick={() => {
                                setLayoutFor(d);
                              }}
                            >
                              {isAdmin ? "ສ້າງ / ແກ້" : "ເບິ່ງ"}
                            </button>
                          )}
                        </td>
                        <td className="is-right">
                          {isAdmin ? (
                            <div
                              className="row"
                              style={{ justifyContent: "flex-end" }}
                            >
                              <button
                                type="button"
                                className="link-action"
                                onClick={() => {
                                  setEditing(d);
                                }}
                              >
                                ແກ້ໄຂ
                              </button>
                              {/* สลับไปสถานะอื่นอีกสองแบบ — ກຳລັງພັດທະນາ = โชว์ในแอปแต่ยังไม่รับคำร้อง */}
                              {(["on", "development", "off"] as const)
                                .filter((m) => m !== docTypeMode(d))
                                .map((m) => (
                                  <button
                                    key={m}
                                    type="button"
                                    className="link-action"
                                    disabled={save.isPending}
                                    onClick={() => {
                                      save.mutate(setModeInput(d, m));
                                    }}
                                  >
                                    {m === "on" ? "ເປີດ" : m === "development" ? "ພັດທະນາ" : "ປິດ"}
                                  </button>
                                ))}
                              {/* ลบได้เฉพาะชนิดที่ไม่เคยมีคำร้อง — เคยมีแล้ว server ปฏิเสธ (409) และบอกให้กด ปິດ */}
                              <button
                                type="button"
                                className="link-action"
                                disabled={remove.isPending || save.isPending}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `ລຶບເອກະສານ ${d.name_lo} (${d.code}) ແທ້ບໍ?\nລຶບໄດ້ສະເພາະເອກະສານທີ່ບໍ່ເຄີຍມີຄຳຮ້ອງ — ຖ້າເຄີຍມີແລ້ວໃຫ້ກົດ ປິດ ແທນ`,
                                    )
                                  ) {
                                    remove.mutate(d.id);
                                  }
                                }}
                              >
                                ລຶບ
                              </button>
                            </div>
                          ) : (
                            <span className="lock-note">
                              <Lock size={13} />
                              ອ່ານໄດ້ຢ່າງດຽວ
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  }),
                ])
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {editing ? (
        <EditDocumentTypeModal
          key={editing.id}
          doc={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
      {layoutFor ? (
        <DocumentLayoutModal
          key={layoutFor.id}
          doc={layoutFor}
          template={templates.data?.byType.get(layoutFor.id)}
          standardFields={templates.data?.standardFields ?? []}
          canEdit={isAdmin}
          onClose={() => {
            setLayoutFor(null);
          }}
        />
      ) : null}
      {schemaFor ? (
        <FormSchemaEditor
          key={schemaFor.id}
          doc={schemaFor}
          template={templates.data?.byType.get(schemaFor.id)}
          reservedKeys={(templates.data?.standardFields ?? []).map(
            (f) => f.key,
          )}
          canEdit={isAdmin}
          onClose={() => {
            setSchemaFor(null);
          }}
        />
      ) : null}
      {creating ? (
        <CreateDocumentTypeModal
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </div>
  );
}
